import { Alert, AlertIcon, Box, Flex, Input, InputGroup, InputRightAddon, Select } from "@chakra-ui/react"
import {
  biodiversityAreaUnitOptionsNb,
  biodiversitySiteProximityOptionsNb,
  countryOptionsNb,
  hazardousWasteTypeOptionsNb,
  nonHazardousWasteTypeOptionsNb,
  pollutantMediaOptionsNb,
  pollutantOptionsNb,
  riskLevelOptionsNb,
  riskTimeTermOptionsNb,
  scope3CategoryOptionsNb,
  sustainabilityIssuesOptionsNb,
  wasteUnitsOptionsNb,
  workforceCountingMethodOptions,
  yesNoOptionsNb,
} from "@esgt/helpers"
import type { QuestionInput } from "@esgt/types"
import { MethodI18nFragment, MethodI18nText } from "components/I18nText"
import { InputNumber } from "components/Input/Number"
import { Controller, useFormContext } from "react-hook-form"
import { createRegexFromValidationPattern } from "./createRegexFromValidationPattern"

interface ShortTextInputProps {
  questionInput: QuestionInput
  disabled: boolean
  inputName: string
  saveChanges: () => void
}

const selectSources: Record<string, Array<{ code: string; name: string }>> = {
  countriesNb: countryOptionsNb,
  workforceCountingMethods: workforceCountingMethodOptions,
  pollutantsNb: pollutantOptionsNb,
  pollutantMediaNb: pollutantMediaOptionsNb,
  biodiversitySiteProximityNb: biodiversitySiteProximityOptionsNb,
  biodiversityAreaUnitsNb: biodiversityAreaUnitOptionsNb,
  wasteUnitsNb: wasteUnitsOptionsNb,
  hazardousWasteTypesNb: hazardousWasteTypeOptionsNb,
  nonHazardousWasteTypesNb: nonHazardousWasteTypeOptionsNb,
  yesNoNb: yesNoOptionsNb,
  sustainabilityIssuesNb: sustainabilityIssuesOptionsNb,
  riskLevelsNb: riskLevelOptionsNb,
  riskTimeTermsNb: riskTimeTermOptionsNb,
  scope3CategoriesNb: scope3CategoryOptionsNb,
}

export const ShortTextInput: React.FC<ShortTextInputProps> = ({ questionInput, disabled, inputName, saveChanges }) => {
  const {
    register,
    control,
    formState: { isValid },
  } = useFormContext()

  const { ...registeredProps } = register(inputName, {
    pattern: createRegexFromValidationPattern(questionInput.validationPattern),
    onChange: saveChanges,
  })

  return (
    <Flex flexDirection="column" gap={2}>
      <Box maxWidth={questionInput.size === "sm" ? "230px" : "400px"}>
        <InputGroup size="sm">
          {questionInput.inputType === "select" && questionInput.selectSource ? (
            <Select
              isDisabled={disabled}
              bgColor="white"
              placeholder="— Velg —"
              size="sm"
              {...register(inputName, { onChange: saveChanges })}
            >
              {(selectSources[questionInput.selectSource] ?? []).map((option) => (
                <option key={option.code} value={option.code}>
                  {option.name}
                </option>
              ))}
            </Select>
          ) : questionInput.inputType === "number" ? (
            <Controller
              control={control}
              name={inputName}
              render={({ field: { ref, value, ...field } }) => (
                <InputNumber
                  {...field}
                  value={value === null ? "" : value}
                  size="sm"
                  ref={ref}
                  isDisabled={disabled}
                  bgColor="white"
                  precision={questionInput.precision ?? 2}
                  borderRightRadius="0"
                  thousands={questionInput.thousands !== false}
                  hasError={!isValid}
                />
              )}
            />
          ) : (
            <Input isDisabled={disabled} bgColor="white" {...registeredProps} type="text" isInvalid={!isValid} />
          )}

          {questionInput.unit && (
            <InputRightAddon>
              <MethodI18nFragment text={questionInput.unit} />
            </InputRightAddon>
          )}
        </InputGroup>
      </Box>

      {!isValid && questionInput.invalidMessage && (
        <Alert status="error">
          <AlertIcon /> <MethodI18nText text={questionInput.invalidMessage} />
        </Alert>
      )}
    </Flex>
  )
}
